import { IconCircleDashedMinus } from "@tabler/icons-react"
import Decimal from "decimal.js-light"
import { type FC, useEffect, useLayoutEffect, useMemo, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router"
import styled from "styled-components"

import { type Bundle, type BundleProductType } from "@forento/core/models/bundle"
import { parseNumber } from "@forento/core/utilities/number"
import { useFile } from "@forento/ui/hooks/useFile"

import Button from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import { AddCircleIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import RichContentEditor from "~/components/RichContentEditor"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform } from "~/contexts/UserContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import usePrice from "~/hooks/usePrice"
import {
	exportEditedRichContent,
	loadRichContent,
	parseRichContent,
	type RichContentValue,
} from "~/utilities/richContent"
import routes from "~/utilities/routes"
import { componentBackgroundColor, componentBorderColor } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

const EditBundlePage = withAccessRequirement(
	"bundles",
	<PageBreadcrumb path={[{ title: "Bundles", link: routes.bundle.index() }]} title="..." />,
	() => {
		const bundleId = parseNumber(useParams().bundleId)

		const { data, error, refetch } = query.bundle.get.useQuery(bundleId ?? -1)
		const courses = query.course.list.useQuery()
		const downloadables = query.downloadable.list.useQuery()
		const videos = query.video.list.useQuery()
		const sessions = query.privateSession.list.useQuery()
		const events = query.event.list.useQuery()

		if (data === null) {
			return <Navigate to={routes.bundle.index()} replace />
		}

		return (
			<Layout>
				<PageBreadcrumb
					path={[{ title: "Bundles", link: routes.bundle.index() }]}
					title={data?.name ?? "..."}
				/>
				{error || courses.error || downloadables.error || videos.error || sessions.error || events.error ? (
					<p>Failed to load bundle.</p>
				) : !data || !courses.data || !downloadables.data || !videos.data || !sessions.data || !events.data ? (
					<PartialLoadingPage />
				) : (
					<Content
						bundle={data}
						reload={refetch}
						itemsToAdd={[
							{
								name: "Courses",
								type: "course",
								items: courses.data
									.filter(x => x.price && !x.isManualOnly)
									.map(x => ({ id: x.id, name: x.title, price: x.price!.amount })),
							},
							{
								name: "Videos",
								type: "video",
								items: videos.data
									.filter(x => x.price && !x.isManualOnly)
									.map(x => ({ id: x.id, name: x.name, price: x.price!.amount })),
							},
							{
								name: "Downloadables",
								type: "downloadable",
								items: downloadables.data
									.filter(x => x.price && !x.isManualOnly)
									.map(x => ({ id: x.id, name: x.name, price: x.price!.amount })),
							},
							{
								name: "Sessions",
								type: "session",
								items: sessions.data
									.filter(x => x.price && !x.isManualOnly)
									.map(x => ({ id: x.id, name: x.title, price: x.price!.amount })),
							},
							{
								name: "Events",
								type: "event",
								items: events.data
									.filter(x => x.price && !x.isManualOnly && x.startDate > new Date())
									.map(x => ({ id: x.id, name: x.title, price: x.price!.amount })),
							},
						]}
					/>
				)}
			</Layout>
		)
	},
)

type ContentProps = {
	bundle: Bundle
	itemsToAdd: { name: string; type: BundleProductType; items: { id: number; name: string; price: number }[] }[]
	reload(): Promise<unknown>
}
const Content: FC<ContentProps> = ({ bundle, itemsToAdd, reload }) => {
	const platform = usePlatform()!
	const alert = useAlert()
	const navigate = useNavigate()
	const toast = useToast()

	const [name, setName] = useState(bundle.name)
	const { price, priceValue, setPrice } = usePrice(bundle.price.amount)
	const [isPaused, setPaused] = useState(bundle.pauseDate !== null)
	const [shortDescription, setShortDescription] = useState(bundle.shortDescription ?? "")
	const longDescription = useRichTextArea({ label: "Description", initialValue: bundle.longDescription })
	const [presentation, setPresentation] = useState<RichContentValue | null>(loadRichContent(bundle.presentation))
	const [addedItems, setAddedItems] = useState<
		{ id: number; type: BundleProductType; name: string; price: number }[]
	>([])

	const thumbnail = useFile(bundle.id, bundle.thumbnailUrl)

	useLayoutEffect(() => {
		setName(bundle.name)
		setPrice(bundle.price.amount.toString())
		setPaused(bundle.pauseDate !== null)
		setShortDescription(bundle.shortDescription ?? "")
		setPresentation(loadRichContent(bundle.presentation))
		setAddedItems([])
	}, [bundle, setPrice])

	useEffect(() => {
		const isModified =
			name.trim() !== bundle.name.trim() ||
			priceValue !== bundle.price.amount ||
			isPaused !== (bundle.pauseDate !== null) ||
			shortDescription.trim() !== (bundle.shortDescription ?? "").trim() ||
			longDescription.isModified ||
			presentation?.isModified ||
			thumbnail.isModified ||
			addedItems.length > 0

		if (isModified) {
			toast.setUnsavedChanges(async () => {
				if (name.trim().length === 0 || priceValue === null) {
					await alert.show("Error", "Please fill in all required fields.")
					return false
				}

				const contentPrice = [...bundle.items, ...addedItems].reduce(
					(value, current) => value.add(current.price),
					new Decimal(0),
				)
				if (priceValue >= contentPrice.toNumber()) {
					await alert.show("Error", "Price should be less than the sum of the prices of the included items.")
					return false
				}

				try {
					await trpc.bundle.update.mutate({
						id: bundle.id,
						data: {
							name,
							thumbnailDataUrl: await thumbnail.exportToDataUrl(),
							shortDescription,
							presentation: exportEditedRichContent(presentation),
							longDescription: await longDescription.exportEditorState(),
							price: priceValue,
							isPaused,
							addedItems: addedItems.map(x => ({ id: x.id, type: x.type })),
						},
					})
					await reload()
					return true
				} catch (error) {
					console.error(error)
					await alert.show("Error", "Failed to save bundle.")
				}

				return false
			})
		} else {
			toast.clearUnsavedChanges()
		}
	}, [
		addedItems,
		addedItems.length,
		alert,
		bundle,
		longDescription,
		presentation,
		name,
		navigate,
		priceValue,
		isPaused,
		reload,
		setPrice,
		shortDescription,
		thumbnail,
		toast,
	])

	const initialPresentation = useMemo(() => parseRichContent(bundle.presentation), [bundle.presentation])

	return (
		<Container>
			<InputField label="Name" value={name} onChange={setName} />
			<InputField label={`Price (${platform.currency})`} value={price} onChange={setPrice} />
			<div>
				<Checkbox label="Pause selling of product" isChecked={isPaused} onChange={setPaused} />
				<CheckboxDescription>
					Pausing your bundle stops new purchases, but existing customers and members with access can still
					view it. Others won't see the bundle at all. If the bundle is part of a membership, remove it from
					the tier to prevent new enrollments.
				</CheckboxDescription>
			</div>
			<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
			{longDescription.element}
			<ImageUploader label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
			<Subtitle>Included products</Subtitle>
			<IncludedProducts>
				{itemsToAdd.map(category => (
					<ContentCategory key={category.name}>
						<ContentCategoryTitle>{category.name}</ContentCategoryTitle>
						<ContentsList>
							{[
								...bundle.items.map(x => ({ id: x.id, type: x.type, name: x.name, isAdded: false })),
								...addedItems.map(x => ({ id: x.id, type: x.type, name: x.name, isAdded: true })),
							]
								.filter(added => added.type === category.type)
								.map(item => (
									<ContentItem key={item.id + (item.isAdded ? "-added" : "")}>
										{item.name}
										{item.isAdded && (
											<ContentItemButton
												onClick={() =>
													setAddedItems(current =>
														current.filter(
															x => !(x.type === item.type && x.id === item.id),
														),
													)
												}
											>
												<ContentItemIcon as={IconCircleDashedMinus} />
											</ContentItemButton>
										)}
									</ContentItem>
								))}
						</ContentsList>
					</ContentCategory>
				))}
			</IncludedProducts>
			<Subtitle>Products to add</Subtitle>
			<IncludedProducts>
				{itemsToAdd.map(category => (
					<ContentCategory key={category.name}>
						<ContentCategoryTitle>{category.name}</ContentCategoryTitle>
						<ContentsList>
							{category.items
								.filter(
									item =>
										![...bundle.items, ...addedItems].find(
											x => x.type === category.type && x.id === item.id,
										),
								)
								.map(item => (
									<ContentItem key={item.id}>
										{item.name}
										<ContentItemButton
											onClick={() =>
												setAddedItems(current => [
													...current,
													{
														id: item.id,
														type: category.type,
														name: item.name,
														price: item.price,
													},
												])
											}
										>
											<ContentItemIcon as={AddCircleIcon} />
										</ContentItemButton>
									</ContentItem>
								))}
						</ContentsList>
					</ContentCategory>
				))}
			</IncludedProducts>
			<Subtitle>Presentation</Subtitle>
			<RichContentEditor
				placeholder="Enter bundle presentation here..."
				initialValue={initialPresentation}
				setValue={setPresentation}
			/>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-bottom: 128px;
`

const CheckboxDescription = styled.p`
	font-size: 14px;
	color: #626262;
	margin: 2px 0 0 28px;
`

const Subtitle = styled.h2`
	font-size: 24px;
	font-weight: 600;
	margin-top: 16px;
`

const IncludedProducts = styled.div`
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
`

const ContentCategory = styled.div`
	flex: 1 0 200px;
`

const ContentCategoryTitle = styled.h3`
	font-weight: 600;
	font-size: 14px;
	margin-bottom: 8px;
`

const ContentsList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const ContentItem = styled.div`
	background-color: ${componentBackgroundColor};
	border: 1px solid ${componentBorderColor};
	border-radius: 8px;
	padding: 8px 12px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
`

const ContentItemButton = styled(Button)`
	width: 24px;
	height: 24px;
`

const ContentItemIcon = styled.div`
	width: 100%;
	height: 100%;
`

export default EditBundlePage
